import { useTranslation } from 'react-i18next';
import { cn } from '@attendance/ui/utils';
import { buttonVariants, RowActions, type RowActionItem } from '@attendance/ui';
import { ChevronDown, Download } from 'lucide-react';

/* ===========================================================================
 * THE ONE EXPORT CONTROL, on every screen in the console that has one.
 *
 * The shape is the reports front door's (`/reports/workspace`): a SOLID BRAND
 * trigger reading `⬇ Export ⌄`, opening a MENU whose items are grouped and
 * whose format items carry their file extension on the trailing edge. Every
 * other export in `apps/web` now draws that same object.
 *
 * WHAT IT REPLACES. "Export a report" was four different controls depending on
 * which screen you were standing on:
 *
 *   • the `ReportShell` pages    a SPLIT GROUP (`⬇ Export │ Excel │ CSV`)
 *                                whose width grew with the number of formats;
 *   • the front door             this menu, built privately inside
 *                                `ReportsWorkspace.tsx`;
 *   • the ERP month-end sheet    a single filled button;
 *   • the weekly schedule        a SECONDARY OUTLINE `HeaderAction` reading
 *                                "Export CSV" — a different colour, a
 *                                different weight and a different word from
 *                                the other three.
 *
 * The menu is the shape that survives, and the front door is why. A split
 * group can only say FORMAT: three peer segments read as three spellings of
 * one file. The front door has to say something a segment cannot —
 * `GET /reports/unified` renders one fixed column set and scopes by date
 * range, site and department alone, so it cannot be told about a view, a
 * status filter or a name search, and only the "This view" item can hand over
 * what is actually on screen. A menu holds that distinction (a heading, a
 * second line, an extension chip); segments flatten it, and handing someone a
 * file with rows the screen never showed is exactly how the wrong figure gets
 * reconciled against a payslip.
 *
 * ---------------------------------------------------------------------------
 * FORMAT vs CONSEQUENCE — the one rule a caller has to know.
 *
 * An action that names a FORMAT ("Excel", "CSV") carries an `extension` and
 * sits under a group heading: the extension is what tells the reader these are
 * one file in three spellings. An action that names a CONSEQUENCE — the ERP
 * month-end batch, the workbook holding both manpower boards, "Export filtered
 * CSV" — carries NO extension and keeps its whole sentence as its label,
 * because the sentence is the only thing that says what will land in the
 * downloads folder. That is the distinction the old split group could not
 * express without truncating it, and in a menu it costs nothing.
 *
 * ---------------------------------------------------------------------------
 * ONE ACTION IS A BUTTON, NOT A MENU OF ONE. A disclosure that opens onto a
 * single choice is a click charged for nothing, and it hides the only verb the
 * screen has behind a word that does not name it. So a single action renders
 * the TRIGGER as a plain filled button that performs it directly — same fill,
 * same box, same radius, same 13.5/600 label, same glyph, minus the caret,
 * which is the honest signal that there is something behind it.
 *
 * THE ONE EXPORT THAT IS NOT THIS CONTROL, recorded so it is a decision and
 * not an oversight: the "Export" chip inside the two SELECTION BARS
 * (`employees/BulkActionBar.tsx`, `worksites/WorksiteSelectionBar.tsx`). That
 * is not a page action — it is one verb among peers ("Set position",
 * "Activate / deactivate") inside a brand-TINTED strip, drawn in that strip's
 * single chip anatomy at `size="sm"`. Dropping a 40px solid-brand trigger into
 * it would put a second brand fill on a brand wash and make one of five peers
 * look like the page's primary. Those bars export the SELECTION, always as one
 * file, and they say so in one word; when one of them ever grows a second
 * format, it takes this control.
 *
 * ---------------------------------------------------------------------------
 * GEOMETRY, and where each value comes from — no figure is copied from a mock.
 *
 *   min-h-10            `headerActionHeight`, the box `HeaderAction` draws, so
 *                       Save view / Schedule / Export sit on one baseline in a
 *                       page head. `size="toolbar"` swaps it for the 38px
 *                       `min-h-control` a filter bar's controls use.
 *   rounded-control     the radius ROLE for a button (§1).
 *   border-transparent  load-bearing: it keeps this the same box as the
 *                       outlines beside it, which pay a pixel a side.
 *   text-copy 600       one type step across the cluster.
 *   shadow-resting      an object sitting on the page (§1's elevation roles).
 *
 * `RowActions` IS the menu, rather than a second one built here (§6). It takes
 * a worded `trigger` precisely so callers can reuse the anatomy instead of
 * re-deriving it, and that anatomy is the part worth not re-deriving:
 * portalled so a Card's `overflow-hidden` cannot clip it, `role="menu"` with
 * roving arrows, `aria-haspopup`/`aria-expanded`, Escape and click-outside
 * closing it, focus returning to the trigger, the menu closed before the
 * action runs. Most of that is invisible until a keyboard user hits it, which
 * is exactly how a hand-rolled copy ships broken — and the front door's
 * private menu was one, with its own click-outside listener and its own
 * hand-written mobile anchoring.
 * ======================================================================== */

/** The header-action glyph, at the one size this control ever draws it. */
const ICON_SIZE = 15;

export interface ExportAction {
  /** Stable key for the action. */
  key: string;
  /**
   * The full sentence — "Excel", "Export the clock-card sheet for 2026-08".
   * It is the accessible name in every shape, and the visible label in both.
   */
  name: string;
  onSelect: () => void;
  /**
   * The file extension, `.xlsx` / `.csv` / `.pdf`, shown muted on the trailing
   * edge. FORMAT ACTIONS ONLY — see the format-vs-consequence rule above.
   */
  extension?: string;
  /** One muted line under the label, for what the label cannot carry. */
  description?: string;
  /** The band this action belongs to, as a heading ("Full report"). */
  group?: string;
  /** In flight. The item says so and cannot be pressed again. */
  busy?: boolean;
  /**
   * WHY THIS ONE action cannot run, when the others still can.
   *
   * The menu-level `blockedBy` is a statement about the whole control, and it
   * is the right shape for "no report has loaded". It is the WRONG shape the
   * moment one row survives the obstacle the others do not: the ERP clock-card
   * menu offers the month's batch AND the empty template, and a month with no
   * billable rows blocks the first while making the second the only thing worth
   * pressing. Blocking both would withhold the column layout exactly when
   * somebody is trying to work out why the month is empty.
   *
   * Takes precedence over the menu-level sentence, so a row that names its own
   * obstacle is never overwritten by the general one.
   */
  blockedBy?: string;
}

export interface ExportMenuProps {
  actions: ExportAction[];
  /**
   * WHY nothing can be exported, as a sentence — "Nothing to export — no
   * report has loaded." Setting it disables the control AND states the
   * obstacle. There is no bare `disabled`, for the reason §3.11 gives: a dead
   * control that does not say why is the worst state in the product, and a
   * dimmed Export with no explanation reads as a permissions problem, sending
   * the reader to hunt for a role instead of retrying the report.
   */
  blockedBy?: string;
  /** The caveat that belongs to the menu rather than to any one item. */
  footnote?: string;
  /**
   * A sentence under a single-action control that is NOT an obstacle.
   *
   * `blockedBy` says why the control cannot run and disables it. This says
   * something the reader needs while it still can — the ERP clock-card control
   * on an empty month hands over the column template rather than the batch, and
   * has to say why the batch is not what they are getting. A control that
   * quietly gives you a different file than the one you expected is worse than
   * one that refuses.
   *
   * `blockedBy` wins when both are set: an obstacle outranks a caveat.
   */
  note?: string;
  /**
   * Which cluster this sits in. `header` (default) is the 40px page-head box
   * beside `HeaderAction`; `toolbar` is the 38px control a filter bar draws.
   * Match the neighbours — two heights in one row is the defect this whole
   * control exists to end.
   */
  size?: 'header' | 'toolbar';
  /**
   * How much weight this export carries on the screen it is on.
   *
   * `primary` (default) is the solid brand fill — right where the export IS
   * what the screen is for: a report is a thing you read, and the one thing a
   * reader does when they have finished reading it is take it away.
   *
   * `quiet` is the outline, for an export that belongs to ONE SECTION of a page
   * that has several. The manpower report writes a sites sheet and a camps
   * sheet from two different tables, and §1 spends the brand on "the primary
   * action" — singular. Two solid fills on one page is not emphasis, it is two
   * screens' worth of emphasis on one, and the reader can no longer tell which
   * of them the page is about. Same control, same box, same glyph, one step
   * down in weight.
   */
  /** `bulk`: a chip on a selection bar, the same object as its neighbours (owner, 2026-11-03). */
  tone?: 'primary' | 'quiet' | 'bulk';
  className?: string;
}

/**
 * The fill, the box and the type of the export control — declared once so no
 * screen can draw a fifth version of it.
 *
 * `disabled:` fades rather than greys: `opacity-50` is the one disabled
 * treatment the primitives use, and `text-foreground-disabled` is ink for a
 * control on a surface, not for a label on a brand fill. Pointer events
 * deliberately survive it — `title` carries the obstacle, and
 * `pointer-events-none` would silence the tooltip that says why it is dead.
 */
export function exportControlClass(
  size: 'header' | 'toolbar' = 'header',
  tone: 'primary' | 'quiet' | 'bulk' = 'primary'
): string {
  // A selection bar's chip: the bar's own outline `sm` button, nothing taller.
  if (tone === 'bulk') return buttonVariants({ variant: 'outline', size: 'sm' });
  /* `Button`'s OWN RECIPE. This used to be eleven hand-written utilities that
     had to stay in step with the solid fill by inspection — and it is the third
     copy of that recipe in this cluster to be folded back in (see
     `SaveViewMenu`). `buttonVariants` is the primitive's class list, exported
     for the elements that genuinely cannot be a `<button>`; this one is a menu
     trigger `RowActions` renders, so it takes the recipe rather than a likeness
     of it.

     The two overrides after it are real differences and not drift:
       • `min-w-action`, the header cluster's width floor, so Export is the same
         object as Save view and Schedule beside it (`--w-action`).
       • `min-h-control` (38px) in a TOOLBAR, where this control stands beside
         filter-bar controls rather than header actions. `cn` is tailwind-merge,
         so the later class genuinely replaces the recipe's `min-h-10`. */
  return cn(
    buttonVariants({ variant: tone === 'quiet' ? 'outline' : 'default', size: 'md' }),
    size === 'header' ? 'min-w-action' : 'min-h-control'
  );
}

export function ExportMenu({
  actions,
  blockedBy,
  note,
  size = 'header',
  tone = 'primary',
  className,
}: ExportMenuProps): JSX.Element | null {
  const { t } = useTranslation();
  if (actions.length === 0) return null;

  const blocked = blockedBy != null && blockedBy !== '';
  const exportWord = t('common.export', 'Export');
  const busyWord = t('reports.shell.exporting', 'Exporting…');
  const triggerClass = exportControlClass(size, tone);

  const only = actions[0];
  if (actions.length === 1 && only) {
    /* THE OBSTACLE IS A SENTENCE ON THE SCREEN, not a `title`.
     *
     * This branch used to carry the reason in `title` alone, and this file's
     * own docblock says why that is not enough: "a control that does not say
     * why is the worst state in the product, and a dimmed Export with no
     * explanation reads as a permissions problem, sending the reader to hunt
     * for a role instead of retrying the report."
     *
     * A `title` is not a sentence on the screen. It needs a hover and a
     * second's patience, it is invisible to a keyboard, and it DOES NOT EXIST
     * ON A TOUCHSCREEN — which is most of where this product is used. The
     * measured case: the ERP clock-card button on a month with no exportable
     * rows drew a faded button and nothing else anywhere on the page, so the
     * reader saw an export that would not work and no reason at all.
     *
     * The MENU branch below already got this right — the obstacle travels to
     * every item as visible text. The rule was stated once, tested for two
     * items, and unenforced for one. Both branches say it out loud now, and
     * `aria-describedby` ties the sentence to the control so it is announced
     * with the button rather than found by luck.
     *
     * `title` stays as well: it is the one affordance that works while the
     * pointer is already on the control, and it costs nothing. */
    // An obstacle outranks a caveat: a control that cannot run says why it
    // cannot, never what it would have done.
    // NO SENTENCE UNDER THE BUTTON (owner, 2026-11-02: "Tablet performance is
    // still loading", "Copies every tablet, not only the 0 shown" — "very
    // wrong, no need, on all pages"). A blocked export is disabled; its reason
    // stays in `title` only.

    return (
      /* THE WRAPPER IS ALWAYS HERE, and `display: contents` is why that costs
         nothing. Returning a bare button when clear and a wrapped one when
         blocked would REMOUNT the control on every transition — a new DOM node
         each time the month finishes loading, which drops focus mid-keyboard
         and makes a held reference to the button go stale. (`ErpApprovalsPage`'s
         own test holds one and waits for it to enable; it caught this.)

         `contents` removes the wrapper from layout entirely, so the button
         participates in the page-head action cluster exactly as it did before.
         When blocked the wrapper becomes a real column: the control keeps its
         corner and the reason wraps UNDER it rather than stretching the header.
         `max-w-identity` is the narrowest declared ceiling in the ramp and the
         right order of magnitude for a one-line obstacle; §7 rejects spelling a
         new one here. */
      <div className="contents">
        <button
          type="button"
          onClick={only.onSelect}
          disabled={blocked || Boolean(only.busy)}
          aria-busy={only.busy || undefined}
          // The reason, or the caveat, travels in `title` only — never as a
          // sentence under the button (owner, 2026-11-02).
          title={blocked ? blockedBy : note}
          className={cn(triggerClass, className)}
        >
          <Download size={ICON_SIZE} aria-hidden="true" />
          {only.busy ? busyWord : only.name}
        </button>
      </div>
    );
  }

  const items: RowActionItem[] = actions.map((action) => ({
    label: action.busy ? busyWord : action.name,
    onSelect: action.onSelect,
    trailing: action.extension,
    description: action.description,
    group: action.group,
    /* The obstacle travels to every item, so a reader who opens the menu is
       told why it cannot be used rather than finding it dimmed (§3.11).
       `busy` blocks with its own sentence: an export already running is a
       different state from one that cannot run. */
    blockedBy:
      action.blockedBy ??
      (blocked
        ? blockedBy
        : action.busy
          ? t('reports.shell.exportInFlight', 'This export is already running.')
          : undefined),
  }));

  return (
    <RowActions
      items={items}
      label={exportWord}
      className={tone === 'bulk' ? undefined : className}
      /* FILLS ITS WRAPPER. In a page header's equal columns the wrapper is
         stretched to the widest action (Save view) while the button kept its
         own width, leaving ~48px of empty column after "Export" — the cluster
         looked pushed off the right edge (owner, 2026-10-15). Inside a
         shrink-wrapped wrapper anywhere else this changes nothing. */
      triggerClassName={tone === 'bulk' ? cn(triggerClass, className) : cn(triggerClass, 'flex-1')}
      trigger={
        <>
          <Download size={ICON_SIZE} aria-hidden="true" />
          {exportWord}
          <ChevronDown size={ICON_SIZE} aria-hidden="true" />
        </>
      }
    />
  );
}
